/**
 * WorkshopModals Component - All modal dialogs for workshop management
 * Replicates Admin Panel content upload functionality for practitioners
 */
import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, Percent, X, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { calculatePricing } from './WorkshopUtils';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

// ===== TAG INPUT COMPONENT =====

const TagInput = ({ tags = [], availableTags = [], onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const filteredSuggestions = availableTags.filter(
    tag => tag.toLowerCase().includes(inputValue.toLowerCase()) && !tags.includes(tag)
  ).slice(0, 10);
  
  const addTag = (tag) => {
    const normalizedTag = tag.trim().toLowerCase();
    if (normalizedTag && !tags.includes(normalizedTag)) {
      onChange([...tags, normalizedTag]);
    }
    setInputValue('');
    setShowSuggestions(false);
  };
  
  const removeTag = (tagToRemove) => {
    onChange(tags.filter(tag => tag !== tagToRemove));
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };
  
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 mb-2">
        {tags.map(tag => (
          <Badge key={tag} variant="secondary" className="flex items-center gap-1">
            {tag}
            <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
          </Badge>
        ))}
      </div>
      <div className="relative">
        <Input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="Type to add tags (press Enter to add)"
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
            {filteredSuggestions.map(tag => (
              <div
                key={tag}
                className="px-3 py-2 cursor-pointer hover:bg-slate-700"
                onMouseDown={() => addTag(tag)}
              >
                {tag}
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500">Add keywords to help users find your workshop. Type and press Enter, or select from existing tags.</p>
    </div>
  );
};

// ===== CATEGORY INPUT COMPONENT (Practitioner-driven like Tags) =====

const CategoryInput = ({ 
  selectedCategory, 
  categories = [], 
  onChange, 
  onCreateCategory 
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);
  
  const filteredCategories = categories.filter(
    cat => cat.name.toLowerCase().includes(inputValue.toLowerCase())
  );
  
  const selectedCategoryName = categories.find(c => c.id === selectedCategory)?.name || '';
  
  // Displayed value: show input value if typing, else selected category name
  const displayValue = inputValue || (showDropdown ? '' : selectedCategoryName);
  
  const handleCreate = async () => {
    if (!inputValue.trim()) return;
    
    setCreating(true);
    try {
      const newCategory = await onCreateCategory(inputValue.trim());
      if (newCategory?.id) {
        onChange(newCategory.id);
        setInputValue('');
        setShowDropdown(false);
      }
    } finally {
      setCreating(false);
    }
  };
  
  const handleSelect = (cat) => {
    onChange(cat.id);
    setInputValue('');
    setShowDropdown(false);
  };
  
  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={displayValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => {
          setShowDropdown(true);
          // Clear display to show all options
          if (selectedCategoryName && !inputValue) {
            setInputValue('');
          }
        }}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && inputValue.trim() && !filteredCategories.some(c => c.name.toLowerCase() === inputValue.toLowerCase())) {
            e.preventDefault();
            handleCreate();
          }
        }}
        placeholder="Type to search or create category..."
        className="w-full"
      />
      
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {filteredCategories.length > 0 ? (
            filteredCategories.map(cat => (
              <div
                key={cat.id}
                className={`px-3 py-2 cursor-pointer hover:bg-slate-700 ${selectedCategory === cat.id ? 'bg-slate-700' : ''}`}
                onMouseDown={() => handleSelect(cat)}
              >
                {cat.name}
              </div>
            ))
          ) : !inputValue.trim() ? (
            <div className="px-3 py-2 text-gray-500 text-sm">
              No categories yet. Type to create one.
            </div>
          ) : null}
          
          {inputValue.trim() && !filteredCategories.some(c => c.name.toLowerCase() === inputValue.toLowerCase()) && (
            <div
              className="px-3 py-2 cursor-pointer hover:bg-cyan-700 bg-cyan-800/50 border-t border-slate-600 flex items-center gap-2"
              onMouseDown={handleCreate}
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span>+ Create &quot;{inputValue.trim()}&quot;</span>
              )}
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-gray-500 mt-1">Type to search existing or create new category (press Enter)</p>
    </div>
  );
};

// ===== COVER IMAGE UPLOAD COMPONENT =====

const CoverImageInput = ({ imageUrl, onUrlChange, onFileSelect, workshopId }) => {
  const fileInputRef = useRef(null);
  // Track locally selected file preview separately from imageUrl prop
  const [localPreview, setLocalPreview] = useState('');
  
  // Use imageUrl if no local preview, otherwise use local preview
  const previewUrl = localPreview || imageUrl || '';
  
  const clearPreview = () => {
    setLocalPreview('');
    onUrlChange('');
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (jpg, png, gif, webp)');
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setLocalPreview(e.target.result);
      reader.readAsDataURL(file);
      
      // Call parent handler
      if (onFileSelect) {
        onFileSelect(file);
      }
    }
  };
  
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={imageUrl || ''}
          onChange={(e) => {
            onUrlChange(e.target.value);
            setLocalPreview(''); // Clear local preview when URL is manually entered
          }}
          placeholder="Enter image URL or upload file..."
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-4 h-4 mr-1" /> Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
      
      {previewUrl && (
        <div className="relative w-full h-32 bg-slate-800 rounded-lg overflow-hidden">
          <img 
            src={previewUrl} 
            alt="Cover preview" 
            className="w-full h-full object-cover"
            onError={clearPreview}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute top-1 right-1 bg-black/50 hover:bg-black/70"
            onClick={clearPreview}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      
      <p className="text-xs text-gray-500">Supported: jpg, jpeg, png, gif, webp (max 10MB)</p>
    </div>
  );
};

// ===== WORKSHOP CREATE/EDIT MODAL =====

export const WorkshopModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  categories,
  availableTags = [],
  isEditing,
  onSave,
  onCoverUpload,
  onCreateCategory,
  prerequisiteOptions = { workshops: [], modules: [] }
}) => {
  const [feePct, setFeePct] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (form.platform_fee_percent !== undefined && form.platform_fee_percent !== null) {
      setFeePct(form.platform_fee_percent);
      return;
    }
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/workshops/platform-fee`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => setFeePct(r.data.default_percent))
      .catch(() => setFeePct(undefined));
  }, [open, form.platform_fee_percent]);

  const pricing = calculatePricing(form.price, feePct ?? undefined);
  const [pendingCoverFile, setPendingCoverFile] = useState(null);
  
  const handleSave = async () => {
    await onSave();
    // If there's a pending cover file and we're editing, upload it
    if (pendingCoverFile && onCoverUpload) {
      await onCoverUpload(pendingCoverFile);
      setPendingCoverFile(null);
    }
  };
  
  // Toggle workshop prerequisite
  const toggleWorkshopPrereq = (workshopId) => {
    const current = form.prerequisite_workshops || [];
    const updated = current.includes(workshopId)
      ? current.filter(id => id !== workshopId)
      : [...current, workshopId];
    setForm({ ...form, prerequisite_workshops: updated });
  };
  
  // Toggle module prerequisite
  const toggleModulePrereq = (moduleId) => {
    const current = form.prerequisite_modules || [];
    const updated = current.includes(moduleId)
      ? current.filter(id => id !== moduleId)
      : [...current, moduleId];
    setForm({ ...form, prerequisite_modules: updated });
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Workshop' : 'Create New Workshop'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Workshop title"
            />
          </div>
          
          <div>
            <Label>Description *</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe your workshop..."
              rows={4}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Your Price ($) *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="pl-10"
                  placeholder="29.99"
                />
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <CategoryInput
                selectedCategory={form.category_id}
                categories={categories}
                onChange={(id) => setForm({ ...form, category_id: id })}
                onCreateCategory={onCreateCategory}
              />
            </div>
          </div>
          
          {/* Pricing breakdown */}
          {form.price && parseFloat(form.price) > 0 && (
            <div className="p-3 bg-slate-800 rounded-lg text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-gray-400">Platform fee ({pricing.platformFeePct}%):</span>
                <span className="text-gray-100" data-testid="workshop-platform-fee">-${pricing.platformFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-1 font-medium">
                <span className="text-gray-400">You receive:</span>
                <span className="text-emerald-300" data-testid="workshop-you-receive">${pricing.youReceive.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-1 pt-1 border-t border-slate-600">
                <span className="text-gray-400">Processing, paid by buyer (3% + $0.30):</span>
                <span className="text-gray-100" data-testid="workshop-processing-fee">${pricing.processingFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-gray-200">Users will pay:</span>
                <span className="text-cyan-400" data-testid="workshop-buyer-total">${pricing.total.toFixed(2)}</span>
              </div>
            </div>
          )}
          
          {/* Search Tags */}
          <div>
            <Label>Search Tags</Label>
            <TagInput
              tags={form.tags || []}
              availableTags={availableTags}
              onChange={(tags) => setForm({ ...form, tags })}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Access Type</Label>
              <Select
                value={form.access_type}
                onValueChange={(v) => setForm({ ...form, access_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lifetime">Lifetime Access</SelectItem>
                  <SelectItem value="time_limited">Time Limited</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Membership Tier Required</Label>
              <Select
                value={form.membership_tier || 'any'}
                onValueChange={(v) => setForm({ ...form, membership_tier: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Tier</SelectItem>
                  <SelectItem value="silver">Silver Tier+</SelectItem>
                  <SelectItem value="gold">Gold Tier+</SelectItem>
                  <SelectItem value="platinum">Platinum Tier Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {form.access_type === 'time_limited' && (
            <div>
              <Label>Duration (days)</Label>
              <Input
                type="number"
                min="1"
                value={form.access_duration_days}
                onChange={(e) => setForm({ ...form, access_duration_days: parseInt(e.target.value) })}
              />
            </div>
          )}
          
          {/* Pre-requisite Workshops */}
          <div>
            <Label>Pre-requisite Workshops (optional)</Label>
            <p className="text-xs text-gray-500 mb-2">Users must complete these workshops before purchasing</p>
            <div className="max-h-32 overflow-y-auto border border-slate-600 rounded p-2 bg-slate-800">
              {prerequisiteOptions.workshops?.length > 0 ? (
                prerequisiteOptions.workshops.map(ws => (
                  <label key={ws.id} className="flex items-center gap-2 p-1 hover:bg-slate-700 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(form.prerequisite_workshops || []).includes(ws.id)}
                      onChange={() => toggleWorkshopPrereq(ws.id)}
                      className="rounded border-gray-600"
                    />
                    <span className="text-sm text-white">{ws.title}</span>
                    <span className="text-xs text-gray-400">by {ws.creator_name}</span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-400 italic">No approved workshops available</p>
              )}
            </div>
          </div>
          
          {/* Pre-requisite Learning Modules */}
          <div>
            <Label>Pre-requisite Learning Modules (optional)</Label>
            <p className="text-xs text-gray-500 mb-2">Users must complete these modules before purchasing</p>
            <div className="max-h-32 overflow-y-auto border border-slate-600 rounded p-2 bg-slate-800">
              {prerequisiteOptions.modules?.length > 0 ? (
                prerequisiteOptions.modules.map(mod => (
                  <label key={mod.id} className="flex items-center gap-2 p-1 hover:bg-slate-700 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(form.prerequisite_modules || []).includes(mod.id)}
                      onChange={() => toggleModulePrereq(mod.id)}
                      className="rounded border-gray-600"
                    />
                    <span className="text-sm text-white">{mod.title}</span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-400 italic">No published modules available</p>
              )}
            </div>
          </div>
          
          <div>
            <Label>Cover Image</Label>
            <CoverImageInput
              imageUrl={form.image_url}
              onUrlChange={(url) => setForm({ ...form, image_url: url })}
              onFileSelect={setPendingCoverFile}
            />
          </div>

          <div>
            <Label>Preview Clip Link (optional)</Label>
            <Input
              value={form.preview_video_url || ''}
              onChange={(e) => setForm({ ...form, preview_video_url: e.target.value })}
              placeholder="https://youtu.be/… or https://vimeo.com/…"
              className="bg-slate-700 border-slate-600 text-white mt-1"
              data-testid="workshop-preview-video-input"
            />
            <p className="text-xs text-gray-400 mt-1">
              A short taster — keep it under 3 minutes. Paste a YouTube or Vimeo link (or a direct .mp4 link).
              Members see a play button on your cover image and the clip plays right there.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700">
            {isEditing ? 'Save Changes' : 'Create Workshop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===== COLLABORATOR INVITE MODAL =====

export const CollaboratorModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  practitioners,
  onSave
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Invite Collaborator</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Select Practitioner</Label>
          <Select
            value={form.user_id}
            onValueChange={(v) => setForm({ ...form, user_id: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a practitioner" />
            </SelectTrigger>
            <SelectContent>
              {practitioners.map(p => (
                <SelectItem key={p.user?.id} value={p.user?.id}>
                  {p.user?.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Income Share (%)</Label>
          <div className="relative">
            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              type="number"
              min="1"
              max="99"
              value={form.percentage}
              onChange={(e) => setForm({ ...form, percentage: parseInt(e.target.value) })}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">This will be deducted from your share</p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSave}>Send Invitation</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ===== MODULE MODAL =====

export const ModuleModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  isEditing,
  onSave
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{isEditing ? 'Edit Module' : 'Add Module'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Title *</Label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Module title"
          />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Module description..."
            rows={3}
          />
        </div>
        <div>
          <Label>Order</Label>
          <Input
            type="number"
            min="0"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) })}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSave}>{isEditing ? 'Save' : 'Create'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ===== FOLDER MODAL =====

export const FolderModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  modules,
  isEditing,
  onSave
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{isEditing ? 'Edit Folder' : 'Add Folder'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {!isEditing && (
          <div>
            <Label>Select Module *</Label>
            <Select
              value={form.module_id}
              onValueChange={(v) => setForm({ ...form, module_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a module" />
              </SelectTrigger>
              <SelectContent>
                {modules?.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Title *</Label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Folder title"
          />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Folder description..."
            rows={2}
          />
        </div>
        <div>
          <Label>Order</Label>
          <Input
            type="number"
            min="0"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) })}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSave}>{isEditing ? 'Save' : 'Create'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ===== CONTENT MODAL - REPLICATING ADMIN PANEL PATTERNS =====

export const ContentModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  folders,
  modules,
  isEditing,
  onSave,
  workshopId
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Token for authenticated requests
  const token = localStorage.getItem('token');
  
  // Content type configurations matching Admin Panel
  const contentTypes = {
    video: {
      label: 'Video',
      description: 'YouTube or Vimeo URL',
      icon: '📹',
      hasUrl: true,
      hasFile: false, // Videos are URL-only per user request
      urlPlaceholder: 'https://www.youtube.com/watch?v=... or https://vimeo.com/...'
    },
    meditation: {
      label: 'Meditation/Audio',
      description: 'Audio file for meditation or guided practice',
      icon: '🧘',
      hasUrl: true,
      hasFile: true,
      fileTypes: '.mp3,.wav,.m4a,.aac,.ogg,.flac',
      maxSize: '100MB',
      urlPlaceholder: 'https://external-audio-url.com/...'
    },
    blog: {
      label: 'Blog/Document',
      description: 'PDF or document file',
      icon: '📝',
      hasUrl: false,
      hasFile: true,
      fileTypes: '.pdf,.doc,.docx',
      maxSize: '50MB'
    },
    exercise: {
      label: 'Exercise/Worksheet',
      description: 'Downloadable exercise or worksheet',
      icon: '💪',
      hasUrl: false,
      hasFile: true,
      fileTypes: '.pdf,.doc,.docx,.ppt,.pptx',
      maxSize: '50MB'
    },
    link: {
      label: 'External Link',
      description: 'Link to external resource',
      icon: '🔗',
      hasUrl: true,
      hasFile: false,
      urlPlaceholder: 'https://...'
    }
  };
  
  const currentType = contentTypes[form.content_type] || contentTypes.video;
  
  // Build folder options including "Direct to Module" option
  const folderOptions = [];
  
  // Add module-direct options
  if (modules?.length > 0) {
    modules.forEach(m => {
      folderOptions.push({
        id: `module_${m.id}`,
        label: `📚 ${m.title} (Direct)`,
        isModule: true,
        moduleId: m.id
      });
    });
  }
  
  // Add folder options
  if (folders?.length > 0) {
    folders.forEach(f => {
      folderOptions.push({
        id: f.id,
        label: f.moduleName ? `📁 ${f.moduleName} / ${f.title}` : `📁 ${f.title}`,
        isModule: false
      });
    });
  }
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !workshopId) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Determine upload endpoint based on content type
      let endpoint = '';
      switch (form.content_type) {
        case 'meditation':
          endpoint = `${API}/workshops/${workshopId}/upload-audio`;
          break;
        case 'blog':
        case 'exercise':
          endpoint = `${API}/workshops/${workshopId}/upload-document`;
          break;
        default:
          endpoint = `${API}/workshops/${workshopId}/upload-document`;
      }
      
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      if (response.data.success || response.data.file_url) {
        const fileUrl = response.data.file_url || response.data.file_path;
        setForm({
          ...form,
          file_url: fileUrl,
          content_data: {
            ...form.content_data,
            filename: response.data.original_filename || response.data.filename || file.name
          }
        });
        toast.success('File uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  const handleTargetChange = (value) => {
    if (value.startsWith('module_')) {
      const moduleId = value.replace('module_', '');
      setForm({ ...form, folder_id: null, module_id: moduleId, target: value });
    } else {
      setForm({ ...form, folder_id: value, module_id: null, target: value });
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Content' : 'Add Content'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Target Selection - Module or Folder */}
          {!isEditing && (
            <div>
              <Label>Add To *</Label>
              <Select
                value={form.target || form.folder_id || ''}
                onValueChange={handleTargetChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose module or folder" />
                </SelectTrigger>
                <SelectContent>
                  {folderOptions.map(opt => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Add directly to a module or inside a folder
              </p>
            </div>
          )}
          
          <div>
            <Label>Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Content title"
            />
          </div>
          
          <div>
            <Label>Content Type</Label>
            <Select
              value={form.content_type}
              onValueChange={(v) => setForm({ 
                ...form, 
                content_type: v, 
                file_url: '', 
                content_data: {} 
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(contentTypes).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.icon} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">{currentType.description}</p>
          </div>
          
          {/* VIDEO - URL Only (per user request) */}
          {form.content_type === 'video' && (
            <div>
              <Label>Video URL (YouTube or Vimeo)</Label>
              <Input
                value={form.content_data?.video_url || form.file_url || ''}
                onChange={(e) => setForm({ 
                  ...form, 
                  file_url: e.target.value,
                  content_data: { ...form.content_data, video_url: e.target.value }
                })}
                placeholder={currentType.urlPlaceholder}
              />
              <p className="text-xs text-gray-500 mt-1">
                Paste your YouTube or Vimeo video URL
              </p>
            </div>
          )}
          
          {/* MEDITATION/AUDIO - File Upload + Optional URL */}
          {form.content_type === 'meditation' && (
            <>
              <div>
                <Label>Upload Audio File (MP3, WAV, M4A, AAC)</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.content_data?.filename || ''}
                    placeholder="No file selected"
                    readOnly
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {uploadProgress}%</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-1" /> Upload</>
                    )}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={currentType.fileTypes}
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
                {form.content_data?.filename && (
                  <p className="text-xs text-green-500 mt-1">✓ {form.content_data.filename}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Max {currentType.maxSize}</p>
              </div>
              
              <div>
                <Label>Or paste external URL (optional)</Label>
                <Input
                  value={form.content_data?.audio_url || ''}
                  onChange={(e) => setForm({
                    ...form,
                    file_url: e.target.value,
                    content_data: { ...form.content_data, audio_url: e.target.value }
                  })}
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <Label>Instructions/Text (optional)</Label>
                <Textarea
                  value={form.content_data?.instructions || ''}
                  onChange={(e) => setForm({
                    ...form,
                    content_data: { ...form.content_data, instructions: e.target.value }
                  })}
                  placeholder="Add meditation instructions or text content..."
                  rows={4}
                />
              </div>
            </>
          )}
          
          {/* BLOG/DOCUMENT - File Upload */}
          {form.content_type === 'blog' && (
            <>
              <div>
                <Label>Upload Document (PDF, DOCX)</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.content_data?.filename || ''}
                    placeholder="No file selected"
                    readOnly
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {uploadProgress}%</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-1" /> Upload</>
                    )}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={currentType.fileTypes}
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
                {form.content_data?.filename && (
                  <p className="text-xs text-green-500 mt-1">✓ {form.content_data.filename}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Max {currentType.maxSize}</p>
              </div>
              
              <div>
                <Label>Summary (optional)</Label>
                <Textarea
                  value={form.content_data?.summary || ''}
                  onChange={(e) => setForm({
                    ...form,
                    content_data: { ...form.content_data, summary: e.target.value }
                  })}
                  placeholder="Add a brief summary..."
                  rows={3}
                />
              </div>
            </>
          )}
          
          {/* EXERCISE - File Upload */}
          {form.content_type === 'exercise' && (
            <>
              <div>
                <Label>Upload Exercise Document (PDF, DOCX, PPT)</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.content_data?.filename || ''}
                    placeholder="No file selected"
                    readOnly
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {uploadProgress}%</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-1" /> Upload</>
                    )}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={currentType.fileTypes}
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
                {form.content_data?.filename && (
                  <p className="text-xs text-green-500 mt-1">✓ {form.content_data.filename}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Max {currentType.maxSize}</p>
              </div>
              
              <div>
                <Label>Exercise Instructions (optional)</Label>
                <Textarea
                  value={form.content_data?.instructions || ''}
                  onChange={(e) => setForm({
                    ...form,
                    content_data: { ...form.content_data, instructions: e.target.value }
                  })}
                  placeholder="Add instructions for this exercise..."
                  rows={4}
                />
              </div>
            </>
          )}
          
          {/* EXTERNAL LINK */}
          {form.content_type === 'link' && (
            <>
              <div>
                <Label>External URL *</Label>
                <Input
                  value={form.file_url || ''}
                  onChange={(e) => setForm({ ...form, file_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={form.content_data?.description || ''}
                  onChange={(e) => setForm({
                    ...form,
                    content_data: { ...form.content_data, description: e.target.value }
                  })}
                  placeholder="Describe what this link is about..."
                  rows={3}
                />
              </div>
            </>
          )}
          
          <div>
            <Label>Order</Label>
            <Input
              type="number"
              min="0"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-gray-500 mt-1">Position in the list (lower = first)</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={uploading}>
            {isEditing ? 'Save Changes' : 'Add Content'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
